if (!customElements.get('bundle-products')) {
  customElements.define('bundle-products', class BundleProducts extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
      this.limit = parseInt(this.dataset.limit);
      this.storage = {}; // Bundle data storage: { productId: { variantId: quantity } }
      this.productAddedUnsubscriber = undefined;
      this.scrollbar = this.querySelector('custom-scrollbar');
    }

    connectedCallback() {
      this.productAddedUnsubscriber = subscribe(PUB_SUB_EVENTS.bundleProductAdded, (event) => {
        if (event.sectionId === this.sectionId) {
          this._handleProductAdded(event);
        }
      });

      this._updateButtonStates();
      this._updateCartButtonState();
    }

    disconnectedCallback() {
      if (this.productAddedUnsubscriber) {
        this.productAddedUnsubscriber();
      }
    }

    _handleProductAdded(event) {
      this._updateButtonStates();
      this._updateCartButtonState();
      this.scrollbar?.freeze(300)
    }

    _updateButtonStates() {
      if (!this.sectionId) return;

      const bundleData = this.storage;

      const productCount = Object.keys(bundleData).length;
      const limitReached = this.limit > 0 && productCount >= this.limit;

      const productForms = this.querySelectorAll('product-form');

      productForms.forEach(form => {
        const productId = form.querySelector('[name="product-id"]')?.value ||
                    form.closest('product-card')?.dataset.productId ||
                    form.dataset.productId;

        if (!productId) return;

        const submitButton = form.querySelector('[type="submit"]');
        const buttonText = submitButton?.querySelector('.product-form__submit-text');

        if (!submitButton) return;

        if (bundleData && bundleData[productId]) {
          submitButton.setAttribute('aria-disabled', 'true');
          submitButton.classList.add('disabled');
          if (buttonText) {
            buttonText.textContent = window.bundleStrings?.addedToBundle;
          }
        } else if (limitReached) {
          submitButton.setAttribute('aria-disabled', 'true');
          submitButton.classList.add('disabled');
        } else {
          submitButton.setAttribute('aria-disabled', 'false');
          submitButton.classList.remove('disabled');
          if (buttonText) {
            buttonText.textContent = window.bundleStrings?.addToBundle;
          }
        }
      });
    }

    getBundleData() {
      return this.storage;
    }

    clearBundleData() {
      this.storage = {};

      const bundleCart = this.querySelector('bundle-cart');
      if (bundleCart) {
        bundleCart.clearAllSlots();
      }

      this._updateButtonStates();
      this._updateCartButtonState();
    }

    _updateCartButtonState() {
      const bundleCart = this.querySelector('bundle-cart');
      if (!bundleCart) return;

      const cartButton = bundleCart.querySelector('.bundle-cart__totals button[type="button"]');
      const countElement = bundleCart.querySelector('.bundle-cart__count');

      const productCount = Object.keys(this.storage).length;
      const limitReached = this.limit > 0 && productCount >= this.limit;

      if (countElement) {
        countElement.textContent = productCount;
      }

      if (!cartButton) return;

      if (limitReached) {
        cartButton.removeAttribute('disabled');
        cartButton.setAttribute('aria-disabled', 'false');
      } else {
        cartButton.setAttribute('disabled', 'disabled');
        cartButton.setAttribute('aria-disabled', 'true');
      }
    }
  });
}

if (!customElements.get('bundle-cart')) {
  customElements.define('bundle-cart', class BundleCart extends HTMLElement {
    constructor() {
      super();
      this.isMobile = false;
      this.productAddedUnsubscriber = undefined;
      this._handleResize = this._handleResize.bind(this);
      this._handleScroll = this._handleScroll.bind(this);
      this._handleToggle = this._handleToggle.bind(this);
            
      this._scrollRafId = null;
      this._lastParentRect = null;
      this._lastThisRect = null;
            
      this._intersectionObserver = null;
      this._isInViewport = false;
    }

    connectedCallback() {
      this._toggleEl = this.querySelector('[data-bundle-toggle]');
      this._parentGrid = this.closest('.bundle-grid');
      this._productsContainer = this.querySelector('.bundle-cart__products');
      this._errorMessageWrapper = this.querySelector('.bundle-cart__error-message-wrapper');
      this._errorMessage = this._errorMessageWrapper?.querySelector('.bundle-cart__error-message');

      window.addEventListener('resize', this._handleResize);

      if (this._toggleEl) {
        this._toggleEl.addEventListener('change', this._handleToggle);
      }

      const bundleProducts = this.closest('bundle-products');
      const sectionId = bundleProducts?.sectionId;

      this.productAddedUnsubscriber = subscribe(PUB_SUB_EVENTS.bundleProductAdded, (event) => {
        if (event.sectionId === sectionId) {
          this._handleProductAdded(event);
        }
      });

      this.addEventListener('click', this._handleRemoveClick.bind(this));
      this.addEventListener('touchend', this._handleRemoveClick.bind(this));

      this.addEventListener('change', this._handleQuantityChange.bind(this));

      const addToCartButton = this.querySelector('.bundle-cart__totals button[type="button"]');
      if (addToCartButton) {
        addToCartButton.addEventListener('click', this._handleAddToCart.bind(this));
      }

      this._handleResize();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._handleResize);
      window.removeEventListener('scroll', this._handleScroll);
            
      if (this._scrollRafId) {
        cancelAnimationFrame(this._scrollRafId);
      }
            
      if (this._intersectionObserver) {
        this._intersectionObserver.disconnect();
      }
            
      if (this._toggleEl) {
        this._toggleEl.removeEventListener('change', this._handleToggle);
      }
      if (this.productAddedUnsubscriber) {
        this.productAddedUnsubscriber();
      }
    }

    _handleResize() {
      this.isMobile = window.DeviceUtils?.isMobile;

      if (this.isMobile) {
        window.addEventListener('scroll', this._handleScroll, { passive: true });
        this._scrollRafId = requestAnimationFrame(() => this._checkSticky());
                
        if (!this._intersectionObserver) {
          this._setupIntersectionObserver();
        }
      } else {
        window.removeEventListener('scroll', this._handleScroll);
                
        if (this._scrollRafId) {
          cancelAnimationFrame(this._scrollRafId);
          this._scrollRafId = null;
        }
                
        if (this._intersectionObserver) {
          this._intersectionObserver.disconnect();
          this._intersectionObserver = null;
        }
                
        this.classList.remove('bundle-cart--full');
      }
    }

    _setupIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: '50px',
        threshold: 0.01
      };

      this._intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this._isInViewport = entry.isIntersecting;

          if (!this._isInViewport) {
            window.removeEventListener('scroll', this._handleScroll);

            if (this._scrollRafId) {
              cancelAnimationFrame(this._scrollRafId);
              this._scrollRafId = null;
            }
          } else {
            window.addEventListener('scroll', this._handleScroll, { passive: true });
          }
        });
      }, options);

      this._intersectionObserver.observe(this);
    }

    _handleScroll() {
      if (!this._isInViewport) return;

      if (this._scrollRafId) {
        cancelAnimationFrame(this._scrollRafId);
      }
            
      this._scrollRafId = requestAnimationFrame(() => {
        this._checkSticky();
        this._scrollRafId = null;
      });
    }

    _handleToggle() {
      if (!this.isMobile) return;

      if (this.isOpen) {
        this.classList.add('bundle-cart--full');
      } else {
        if (this._isInViewport) {
          this._scrollRafId = requestAnimationFrame(() => this._checkSticky());
        }
      }
    }

    _handleRemoveClick(event) {
      const removeButton = event.target.closest('.bundle-remove-button');
      if (!removeButton) return;

      event.preventDefault();
      event.stopPropagation();

      const slot = removeButton.closest('.bundle-cart__product-slot');
      if (!slot) return;

      const productId = slot.dataset.productId;
      if (!productId) return;

      const bundleProducts = this.closest('bundle-products');
      if (!bundleProducts) return;

      if (bundleProducts.storage && bundleProducts.storage[productId]) {
        delete bundleProducts.storage[productId];
      }

      slot.classList.remove('product-added');
      const productWrapper = slot.querySelector('.bundle-cart__product-wrapper');
      if (productWrapper) {
        productWrapper.innerHTML = '';
      }
      delete slot.dataset.productId;

      this._reorganizeSlots();

      bundleProducts._updateButtonStates();
      bundleProducts._updateCartButtonState();

      this._handleErrorMessage();

      this._updateSubtotal();
    }

    _handleQuantityChange(event) {
      const quantityInput = event.target.matches('input[type="number"].quantity__input') ? event.target : null;

      if (!quantityInput) return;

      const productElement = quantityInput.closest('.bundle-cart__product');
      if (!productElement) return;

      const minValue = parseInt(quantityInput.getAttribute('min'), 10) || 1;
      let quantity = parseInt(quantityInput.value, 10);

      if (isNaN(quantity) || quantity < minValue) {
        quantity = minValue;
        quantityInput.value = minValue;
      }

      const productSlot = productElement.closest('.bundle-cart__product-slot');
      const productId = productSlot?.dataset.productId;
      const bundleProducts = this.closest('bundle-products');

      if (bundleProducts && productId && bundleProducts.storage) {
        const variantData = bundleProducts.storage[productId];

        if (typeof variantData === 'object' && !Array.isArray(variantData)) {
          const variantId = Object.keys(variantData)[0];
          if (variantId) {
            bundleProducts.storage[productId][variantId] = quantity;
          }
        } else {
          const variantId = variantData;
          bundleProducts.storage[productId] = { [variantId]: quantity };
        }
      }

      this._updateProductPrice(productElement, quantity);

      this._handleErrorMessage();

      this._updateSubtotal();
    }

    _updateProductPrice(productElement, quantity) {
      const variantPrice = parseInt(productElement.dataset.variantPrice, 10);
      const compareAtPrice = parseInt(productElement.dataset.compareAtPrice, 10);
      const currency = productElement.dataset.currency;
      const moneyFormat = productElement.dataset.moneyFormat;

      if (!variantPrice || !moneyFormat || typeof formatMoneyShopify !== 'function') return;

      const priceWrapper = productElement.querySelector('.cart-item__price-wrapper');
      if (!priceWrapper) return;

      const totalSalePrice = variantPrice * quantity;
      const formattedSalePrice = formatMoneyShopify(totalSalePrice, moneyFormat, currency);

      const saleElements = priceWrapper.querySelectorAll('.price-item--sale');
      saleElements.forEach(el => {
        el.textContent = formattedSalePrice;
      });

      if (compareAtPrice) {
        const totalComparePrice = compareAtPrice * quantity;
        const formattedComparePrice = formatMoneyShopify(totalComparePrice, moneyFormat, currency);

        const compareElements = priceWrapper.querySelectorAll('.price-item--regular:not(.price-item--sale)');
        compareElements.forEach(el => {
          el.textContent = formattedComparePrice;
        });
      }
    }

    _reorganizeSlots() {
      if (!this._productsContainer) return;

      const slots = Array.from(this._productsContainer.querySelectorAll('.bundle-cart__product-slot'));
      const filledSlots = [];

      slots.forEach(slot => {
        if (slot.classList.contains('product-added')) {
          const productWrapper = slot.querySelector('.bundle-cart__product-wrapper');
          const quantityInput = slot.querySelector('.quantity__input');
          const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

          filledSlots.push({
            productContent: productWrapper ? productWrapper.innerHTML : '',
            productId: slot.dataset.productId,
            quantity: quantity
          });
        }
      });

      slots.forEach(slot => {
        slot.classList.remove('product-added');
        const wrapper = slot.querySelector('.bundle-cart__product-wrapper');
        if (wrapper) {
          wrapper.innerHTML = '';
        }
        delete slot.dataset.productId;
      });

      filledSlots.forEach((filled, index) => {
        const targetSlot = slots[index];
        if (targetSlot && filled.productContent) {
          targetSlot.dataset.productId = filled.productId;
          let wrapper = targetSlot.querySelector('.bundle-cart__product-wrapper');
          if (!wrapper) {
            wrapper = document.createElement('div');
            wrapper.className = 'bundle-cart__product-wrapper';
            const placeholder = targetSlot.querySelector('.bundle-cart__placeholder-wrapper');
            if (placeholder) {
              targetSlot.insertBefore(wrapper, placeholder.nextSibling);
            } else {
              targetSlot.appendChild(wrapper);
            }
          }
          wrapper.innerHTML = filled.productContent;

          const quantityInput = wrapper.querySelector('.quantity__input');
          if (quantityInput && filled.quantity) {
            quantityInput.value = filled.quantity;
          }

          targetSlot.classList.add('product-added');
        }
      });
    }

    _handleProductAdded(event) {
      const { html, productId, quantity } = event;
      if (!html || !this._productsContainer) return;

      const existingProduct = this._productsContainer.querySelector(`[data-product-id="${productId}"]`);
      if (existingProduct) return;

      const emptySlot = this._productsContainer.querySelector('.bundle-cart__product-slot:not(.product-added)');
      if (!emptySlot) return;

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      const productWrapper = tempDiv.querySelector('.bundle-cart__product-wrapper');
      const productContent = productWrapper ? productWrapper.innerHTML : html;

      emptySlot.dataset.productId = productId;

      let slotWrapper = emptySlot.querySelector('.bundle-cart__product-wrapper');
      if (!slotWrapper) {
        slotWrapper = document.createElement('div');
        slotWrapper.className = 'bundle-cart__product-wrapper';
        const placeholder = emptySlot.querySelector('.bundle-cart__placeholder-wrapper');
        if (placeholder) {
          emptySlot.insertBefore(slotWrapper, placeholder);
        } else {
          emptySlot.appendChild(slotWrapper);
        }
      }

      slotWrapper.innerHTML = productContent;

      if (quantity && quantity > 1) {
        const productContent = slotWrapper.querySelector('.bundle-cart__product');
        const quantityInput = productContent?.querySelector('.quantity__input');
        if (quantityInput) {
          quantityInput.value = quantity;
          this._updateProductPrice(productContent, quantity);
        }
      }

      emptySlot.classList.add('product-added');

      this._handleErrorMessage();

      this._updateSubtotal();
    }

    async _handleAddToCart(event) {
      event.preventDefault();

      const bundleProducts = this.closest('bundle-products');
      if (!bundleProducts || !bundleProducts.storage) return;

      const storage = bundleProducts.storage;
      const productIds = Object.keys(storage);

      if (productIds.length === 0) return;

      const items = productIds.map(productId => {
        const variantData = storage[productId];
        if (typeof variantData === 'object' && !Array.isArray(variantData)) {
          return Object.entries(variantData).map(([variantId, quantity]) => ({
            id: parseInt(variantId),
            quantity: parseInt(quantity) || 1
          }));
        }
        return {
          id: parseInt(variantData),
          quantity: 1
        };
      }).flat().filter(item => item.id);

      if (items.length === 0) return;

      const addToCartButton = event.currentTarget;
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      addToCartButton.setAttribute('aria-disabled', 'true');
      addToCartButton.setAttribute('disabled', 'disabled');
      addToCartButton.classList.add('loading');
      const spinner = addToCartButton.querySelector('.loading-spinner');
      if (spinner) spinner.classList.remove('hidden');

      if (cart) {
        cart.setActiveElement(document.activeElement);
      }

      const body = {
        items: items
      };

      if (cart) {
        body.sections = cart.getSectionsToRender().map((section) => section.id).join(',');
        body.sections_url = window.location.pathname;
      }

      const config = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(body)
      };

      try {
        const response = await fetch(`${routes.cart_add_url}`, config);
        const data = await response.json();

        if (data.status || data.errors) {
          this._handleError(data);
          await this._updateCartSections(cart);
          return;
        }

        if (typeof publish !== 'undefined') {
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'bundle-products',
            cartData: data
          });
        }

        await this._updateCartSections(cart, data);
        this._handleErrorMessage();

      } catch (error) {
        this._handleError();
      } finally {
        bundleProducts.clearBundleData();
        addToCartButton.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');

        if (cart && cart.classList.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }
      }
    }

    async _updateCartSections(cart, responseData = null) {
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: cartData }
      }));

      if (!cart) return;

      if (responseData) {
        cart.renderContents(responseData);
      } else {
        const sections = cart.getSectionsToRender();
        const sectionFetches = sections.map(section =>
          fetch(`/?section_id=${section.id}`).then(r => r.text())
        );

        const sectionsHtml = await Promise.all(sectionFetches);
        const sectionsObject = {};
        sections.forEach((section, index) => {
          sectionsObject[section.id] = sectionsHtml[index];
        });

        cart.renderContentsFromHTML(sectionsObject);
      }
    }

    _handleError(data) {
      const errorMessage = data?.description || data?.errors;

      if (typeof publish !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'bundle-products',
          errors: data?.errors || data?.description,
          message: data?.message,
        });
      }

      this._handleErrorMessage(errorMessage);
    }

    _handleErrorMessage(errorMessage = false) {
      if (!this._errorMessageWrapper) return;

      this._errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this._errorMessage.textContent = errorMessage;
      }
    }

    clearAllSlots() {
      if (!this._productsContainer) return;

      const slots = this._productsContainer.querySelectorAll('.bundle-cart__product-slot');
      slots.forEach(slot => {
        slot.classList.remove('product-added');
        const productWrapper = slot.querySelector('.bundle-cart__product-wrapper');
        if (productWrapper) {
          productWrapper.innerHTML = '';
        }
        delete slot.dataset.productId;
      });

      this._updateSubtotal();
    }

    _updateSubtotal() {
      const subtotalElements = this.querySelectorAll('[data-bundle-subtotal]');
      if (!subtotalElements.length) return;

      let totalPrice = 0;
      const firstElement = subtotalElements[0];
      let currency = firstElement.dataset.currency;
      let moneyFormat = firstElement.dataset.moneyFormat;

      const addedProducts = this.querySelectorAll('.bundle-cart__product-slot.product-added .bundle-cart__product');

      addedProducts.forEach(product => {
        const variantPrice = parseInt(product.dataset.variantPrice, 10);
        const quantity = parseInt(product.querySelector('.quantity__input')?.value, 10) || 1;

        if (!currency) currency = product.dataset.currency;
        if (!moneyFormat) moneyFormat = product.dataset.moneyFormat;

        totalPrice += variantPrice * quantity;
      });

      const formattedTotal = typeof formatMoneyShopify === 'function' && moneyFormat
        ? formatMoneyShopify(totalPrice, moneyFormat, currency)
        : totalPrice;

      subtotalElements.forEach(element => {
        const showCurrency = element.closest('.totals');
        element.textContent = showCurrency ? `${formattedTotal} ${currency}` : formattedTotal;
      });
    }

    _checkSticky() {
      if (!this._parentGrid || !this.isMobile) return;

      const parentBottom = this._parentGrid.getBoundingClientRect().bottom;
      const thisBottom = this.getBoundingClientRect().bottom;

      const isCurrentlySticky = this.classList.contains('bundle-cart--full');

      const shouldBeSticky = (parentBottom - thisBottom >= 2) || (thisBottom - parentBottom >= 2);

      if (shouldBeSticky === isCurrentlySticky) return;

      this.classList.toggle('bundle-cart--full', shouldBeSticky);
    }

    open() {
      if (this._toggleEl) this._toggleEl.checked = true;
      this._handleToggle();
    }

    close() {
      if (this._toggleEl) this._toggleEl.checked = false;
      this._handleToggle();
    }

    get isOpen() {
      return !!this._toggleEl?.checked;
    }
  });
}
